import Model, { skipStatusHeader } from '~/models/base/Model';
import { encodeStringToBase64 } from '~/modules/helpers/crypto';

const { isArray } = Array;
const toIdsPathAndPayload = userId => {
    return isArray(userId) ? ['bulk', { user_ids: userId }] : [userId];
};

/**
 * class representing User
 * @extends Model
 */
export default class User extends Model {
    static _url = `${Model._url}/users`;

    static get(id) {
        const requestUrl = `/investigators/${id}/`;
        return this.api.get(requestUrl);
    }

    static ping() {
        const requestUrl = `${this._url}/ping/`;
        return this.api.get(requestUrl);
    }

    static isChatEnabled() {
        const requestUrl = `${this._url}/is-chat-enabled/`;
        return this.api.get(requestUrl);
    }

    /**
     * Get timezone of a specified user and/or all timezone options
     * @returns {Promise<Array>}
     * @author rn
     */
    //TODO permit to accept userId in url
    static getTimezoneUserAndOptions() {
        const requestUrl = `${this._url}/timezone/`;
        return this.api.get(requestUrl);
    }

    /**
     * Get number of used users license
     * @returns {Promise}
     * @author rn
     */
    static getUsedLicenses() {
        const requestUrl = `${this._url}/used/`;
        return this.api.get(requestUrl);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    /**
     * Check if current user is a readonly (at least one profile read only)
     * @returns {boolean}
     * @author sst
     */
    static isReadOnly() {
        return !!CURRENT_USER.roles?.newStackProfile.roles.some(
            role => role.read_only
        );
    }

    /**
     * Verify if action is active in permission list
     * @param {string} action - The action to verify
     * @param {Object} [incidentPermissions] - optional object of permissions for profile inside incident
     * @returns {boolean} return true if action has permission
     * @private
     * @author rn
     */
    static _hasActionPermissions(action, incidentPermissions) {
        if (incidentPermissions && incidentPermissions['all_permissions']) {
            return true;
        }

        const permissions =
            incidentPermissions ||
            CURRENT_USER.roles?.newStackProfile.permissions;

        const permission = permissions?.[action];

        if ('undefined' === typeof permission) {
            if (APP_INFO.debug) {
                console.debug('Undefined permission for:', action);
                // console.dir({ incidentPermissions, permissions });
            }
            return false;
        }

        return 1 === permission;
    }

    /**
     * Verify if the current user can execute one or more action given
     * @param {string|Array} actions - Action(s) to check if user can attempt
     * @param {object} incidentPermissions - all user permission for the current incident
     * @returns {boolean} Return true if user can execute given actions
     * @author rn
     */
    static can(actions, incidentPermissions = null) {
        let actionsToCheck = actions;

        if ('string' === typeof actions) {
            actionsToCheck = [actions];
        }

        const permittedActions = actionsToCheck?.filter(action =>
            User._hasActionPermissions(action, incidentPermissions)
        );

        return permittedActions?.length > 0;
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    // todo: looks unused, check its usefulness
    static _checkPermissionStartingWith(prefix, permissions) {
        const groupPermissions = Object.keys(permissions).filter(permission =>
            permission.startsWith(prefix)
        );
        if (groupPermissions.length) {
            return groupPermissions.some(
                permission => 1 === permissions[permission]
            );
        } else {
            if (APP_INFO.debug) {
                console.debug('No permission starts with:', prefix);
                // console.dir({ prefix, permissions });
            }
            return false;
        }
    }

    // todo: looks unused, check its usefulness
    /**
     * @param {string|Array} action
     * @param {Object} incidentPermissions
     * @returns {boolean}
     */
    static cannot(action, incidentPermissions) {
        return !User.can(action, incidentPermissions);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    /**
     * Get all tags in user preferences table
     * @returns {AxiosPromise<any>}
     * @author FM
     */
    static getTags() {
        const requestUrl = `${this._url}/tags/`;
        return this.api.get(requestUrl);
    }

    /**
     * Get the user preference stored in DB according to the given tag
     * @param {string} tag - preference identifier
     * @returns {AxiosPromise<any>}
     * @author rn|mm
     */
    static getPreferences(tag) {
        const requestUrl = `${this._url}/preferences/${tag}/`;
        return this.api.get(requestUrl);
    }

    /**
     * Set a user preferences by tag
     * @param {string} tag - preference identifier
     * @param {*} value - to be stored in the DB as preference
     * @returns {AxiosPromise<any>}
     */
    static setPreferences(tag, value) {
        const requestUrl = `${this._url}/preferences/${tag}/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = value;
        return this.api.put(requestUrl, requestPayload);
    }

    /**
     * Update a user preferences by tag
     * @param {string} tag - preference identifier
     * @param {object} newPrefs - the object with values to assign.
     * @returns {AxiosPromise<any>} the data saved | exception
     * @note ( to use when a 'patch' url endpoint not exist )
     */
    static updatePreference(tag, newPrefs) {
        const insert = prefs => {
            return User.setPreferences(tag, prefs)
                .then(response => {
                    if (200 !== response.status) {
                        throw response;
                    } else {
                        return prefs;
                    }
                })
                .catch(exception => {
                    // console.dir(exception);
                    return exception;
                });
        };
        return User.getPreferences(tag)
            .then(response => {
                const respData = response.data;
                if (200 !== response.status || !respData) {
                    const prevPrefs = respData.data;
                    const updatedPrefs = prevPrefs
                        ? Object.assign({}, prevPrefs, newPrefs)
                        : newPrefs;
                    return insert(updatedPrefs);
                } else {
                    throw response;
                }
            })
            .catch(exception =>
                404 !== exception.response?.status
                    ? exception
                    : insert(newPrefs)
            );
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    static getAvailableOperations(params) {
        const requestUrl = '/operations/';
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = params;
        return this.api.post(requestUrl, requestPayload);
    }

    static getAvailableOperationsIncidentId(params, ws_invoke) {
        const requestUrl = '/operations-incidentId/';
        const requestPayload = params; // << tbd: nullish fallback
        const requestConfig = {};
        if (ws_invoke) {
            requestConfig.headers = { ...skipStatusHeader };
        }
        return this.api.post(requestUrl, requestPayload, requestConfig);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    /**
     * Get user details by id
     * @param {string | int} userId
     * @param {boolean} [showProfiles=false]
     * @returns {Promise<object>} all user info
     * @author rn
     */
    static getDetails(userId, showProfiles) {
        const requestUrl = `${this._url}/${userId}/`;
        const requestPayload = {
            params: {
                show_profile: showProfiles ?? false
            }
        };
        return this.api.get(requestUrl, requestPayload);
    }

    /**
     * Edit user fields
     * @param {string | int} userId
     * @param {object} params - changed fields
     * @returns {Promise<object>} all user info
     * @author rn
     */
    static editUserDetails(userId, params) {
        const requestUrl = `${this._url}/${userId}/`;
        const requestPayload = params ?? {};
        return this.api.patch(requestUrl, requestPayload);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    /**
     * Delete user(s) by id
     * @param {(string|number|Array.<(string|number)>)} userId - id(s)
     * @returns {Promise} return message
     * @author rn
     */
    static delete(userId) {
        const [idOrBulk, requestPayload] = toIdsPathAndPayload(userId);
        const requestUrl = `${this._url}/${idOrBulk}/delete/`;
        return this.api.put(requestUrl, requestPayload);
    }

    /**
     * Restore user(s) by id
     * @param {(string|number|Array.<(string|number)>)} userId - id(s)
     * @returns {Promise} return message
     * @author rn
     */
    static restore(userId) {
        const [idOrBulk, requestPayload] = toIdsPathAndPayload(userId);
        const requestUrl = `${this._url}/${idOrBulk}/restore/`;
        return this.api.put(requestUrl, requestPayload);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    /**
     * Lock user(s) by id
     * @param {(string|number|Array.<(string|number)>)} userId - id(s)
     * @returns {Promise} return message
     * @author rn
     */
    static lock(userId) {
        const [idOrBulk, requestPayload] = toIdsPathAndPayload(userId);
        const requestUrl = `${this._url}/${idOrBulk}/lock/`;
        return this.api.put(requestUrl, requestPayload);
    }

    /**
     * Unlock user(s) by id
     * @param {(string|number|Array.<(string|number)>)} userId - id(s)
     * @returns {Promise} return message
     * @author rn
     */
    static unlock(userId) {
        const [idOrBulk, requestPayload] = toIdsPathAndPayload(userId);
        const requestUrl = `${this._url}/${idOrBulk}/unlock/`;
        return this.api.put(requestUrl, requestPayload);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    /**
     * Set an expiration date to user(s) that will lock him after 30 days
     * @param {(string|number|Array.<(string|number)>)} userId - id(s)
     * @returns {Promise} return message
     */
    static setExpire(userId) {
        const [idOrBulk, requestPayload] = toIdsPathAndPayload(userId);
        const requestUrl = `${this._url}/${idOrBulk}/expire/`;
        return this.api.put(requestUrl, requestPayload);
    }

    /**
     * Unset the expiration date to user(s)
     * @param {(string|number|Array.<(string|number)>)} userId - id(s)
     * @returns {Promise} return message
     */
    static unsetExpire(userId) {
        const [idOrBulk, requestPayload] = toIdsPathAndPayload(userId);
        const requestUrl = `${this._url}/${idOrBulk}/remove-expire/`;
        return this.api.put(requestUrl, requestPayload);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    /**
     * Get jwt token info of a given user
     * @param {string | int} userId
     * @returns {Promise<object>}
     * @author rn
     */
    static getJwtToken(userId) {
        const requestUrl = `${this._url}/${userId}/jwt/`;
        return this.api.get(requestUrl);
    }

    /**
     * Create jwt token
     * @param {string | int} userId
     * @returns {Promise} return message
     * @author rn
     */
    static createJwtToken(userId) {
        const requestUrl = `${this._url}/${userId}/jwt/`;
        return this.api.post(requestUrl);
    }

    /**
     * Renew jwt token
     * @param {string | int} userId
     * @returns {Promise} return message
     * @author rn
     */
    static renewJwtToken(userId) {
        const requestUrl = `${this._url}/${userId}/jwt/renew/`;
        return this.api.put(requestUrl);
    }

    /**
     * Revoke jwt token
     * @param {string | int} userId
     * @returns {Promise} return message
     * @author rn
     */
    static revokeJwtToken(userId) {
        const requestUrl = `${this._url}/${userId}/jwt/revoke/`;
        return this.api.put(requestUrl);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    /**
     * Get groups that contain given user
     * @param {string | int} userId - if userId is 0, it doesn't retrieve user timezone, only retrieve options
     * @returns {Promise<Array<Object>>}
     * @author rn
     */
    static getUserGroups(userId) {
        const requestUrl = `${this._url}/${userId}/groups/`;
        return this.api.get(requestUrl);
    }

    /**
     * Remove user from group
     * @param {string | int} userId - user id to remove from group
     * @param {string | int} groupId - id of group to remove user
     * @returns {Promise} return message
     * @author rn
     */
    static removeUserFromGroup(userId, groupId) {
        const requestUrl = `${this._url}/${userId}/groups/${groupId}/`;
        return this.api.delete(requestUrl);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    /**
     * Change user(s) password
     * @param {(string|number|Array.<(string|number)>)} userId - id(s)
     * @param {string} currentUserPassword - current user password
     * @param {string} newPassword - target user password
     * @returns {Promise} return message
     * @author rn
     */
    static changePassword(userId, currentUserPassword, newPassword) {
        const [idOrBulk, idsPayload] = toIdsPathAndPayload(userId);
        const requestPayload = idsPayload || {};
        const encode = encodeStringToBase64;
        requestPayload.current_user_password = encode(currentUserPassword);
        requestPayload.new_password = encode(newPassword);
        const requestUrl = `${this._url}/${idOrBulk}/change-pwd/`;
        return this.api.put(requestUrl, requestPayload);
    }

    /**
     * Force change user(s) password
     * @param {(string|number|Array.<(string|number)>)} userId - id(s)
     * @returns {Promise} return message
     */
    static forceChangePassword(userId) {
        const [idOrBulk, requestPayload] = toIdsPathAndPayload(userId);
        const requestUrl = `${this._url}/${idOrBulk}/force-change-pwd/`;
        return this.api.put(requestUrl, requestPayload);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    /**
     * Get password policy for recover password page
     * @returns {Promise}
     * @author rn
     */
    static getPolicyForRecoverPassword(resetId) {
        const requestUrl = `${this._url}/reset-pwd/${resetId}/`;
        return this.api.get(requestUrl);
    }

    /**
     * get password policy for recover password page
     * @param {string | int} resetId - id reset sent through email
     * @param {string} password
     * @param {string} confirmPassword
     * @returns {Promise<object>}
     * @author rn
     */
    static recoverResetPassword(resetId, password, confirmPassword) {
        const requestUrl = `${this._url}/reset-pwd/${resetId}/`;
        const requestPayload = {
            password: encodeStringToBase64(password),
            password_confirm: encodeStringToBase64(confirmPassword)
        };
        return this.api.post(requestUrl, requestPayload);
    }

    /**
     * Send email with resetId to change user password
     * @param {Object} params
     * @param {string} params.email
     * @returns {Promise<string>}
     * @author rn
     */
    static sendMailRecoverPassword(params) {
        const requestUrl = `${this._url}/reset-pwd/`;
        // noinspection UnnecessaryLocalVariableJS (tbd: nullish fallback)
        const requestPayload = params;
        return this.api.post(requestUrl, requestPayload);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    enableNewUI() {
        const requestUrl = `/${this.id}/new-interface/enable/`;
        return this.constructor.api.put(requestUrl);
    }

    disableNewUI() {
        const requestUrl = `/${this.id}/new-interface/disable/`;
        return this.constructor.api.put(requestUrl);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    enableSlackChat() {
        const requestUrl = `/${this.id}/slack-chat/enable/`;
        return this.constructor.api.put(requestUrl);
    }

    disableSlackChat() {
        const requestUrl = `/${this.id}/slack-chat/disable/`;
        return this.constructor.api.put(requestUrl);
    }

    // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

    getSettingsPreferences() {
        const requestUrl = `/${this.id}/preferences/`;
        return this.constructor.api.get(requestUrl);
    }

    setLanguage(value) {
        const requestUrl = '/preferences/language/';
        const requestPayload = { value: value };
        return this.constructor.api.put(requestUrl, requestPayload);
    }

    save() {
        this.password = encodeStringToBase64(this.password);
        return super.save();
    }
}
